import {
  execute,
  FilterConditions,
  INSERT,
  SELECT,
  UPDATE,
} from "@/utils/database_module";
import products from "./products";
import { OrderStatus, SourcingRequest, User } from "@/utils/types";

const sourcing = {
  insert: async (data: SourcingRequest) => {
    const sourcingData = { ...data, products: JSON.stringify(data.products) };
    return await INSERT("sourcingRequests", sourcingData);
  },
  update: async (id: number, value: PartialSourcingRequest) => {
    let sourcingData = value;
    if (value.products) {
      sourcingData = { ...value, products: JSON.stringify(value.products) };
    }
    return await UPDATE("sourcingRequests", sourcingData, [
      { column: "id", operator: "=", value: id },
    ]);
  },

  getAll: async (
    status?: number,
    user_id?: number,
    startDate?: string,
    endDate?: string
  ) => {
    // Initialize the base condition to only include non-deleted requests
    const condition = [
      { column: "is_deleted", operator: "=", value: 0 },
    ] as any;

    // Filter by status (ignore if status is 0 or undefined)
    if (status && status != 0)
      condition.push({ column: "status", operator: "=", value: status ?? "" });

    // Filter by specific user
    if (user_id)
      condition.push({
        column: "user_id",
        operator: "=",
        value: user_id ?? "",
      });

    // Filter by start date (requests after or on this date)
    if (startDate)
      condition.push({
        column: "date",
        operator: ">=",
        value: startDate ?? "",
      });

    // Filter by end date (requests before or on this date)
    if (endDate)
      condition.push({ column: "date", operator: "<=", value: endDate ?? "" });

    // Fetch sourcing requests from the database using the above conditions
    const sourcingRequests = (await SELECT("sourcingRequests", {
      conditions: condition,
      order: "date DESC", // Most recent requests first
    })) as SourcingRequest[];

    // If there are any requests, fetch the users associated with them
    const users =
      sourcingRequests && sourcingRequests?.length != 0
        ? ((await execute(
            `SELECT * FROM users WHERE id IN (${sourcingRequests
              .map((r) => r.user_id)
              .join(", ")})`
          )) as User[])
        : [];

    // Fetch all possible statuses (e.g., "Pending", "Approved", etc.)
    const statuses = (await SELECT("order_status", {})) as OrderStatus[];

    // Enhance each sourcing request with product list, user info, and status label
    for (const sourcing of sourcingRequests) {
      // Parse the products JSON string into an actual object/array
      const products = JSON.parse(`${sourcing.products}`);
      sourcing.products = products;

      // Attach user object to the sourcing request
      sourcing.user = users.find((user) => user.id == sourcing.user_id);

      // Attach status object to the sourcing request
      sourcing.status = statuses.find((status) => status.id == sourcing.status);
    }

    // Return the final array of enriched sourcing requests
    return sourcingRequests;
  },

  delete: async (id: number) => {
    return await UPDATE("sourcingRequests", { is_deleted: 1 }, [
      { column: "id", operator: "=", value: id },
    ]);
  },
};

export default sourcing;

type PartialSourcingRequest = {
  status?: OrderStatus | number;
  products?:
    | string
    | {
        name: string;
        img: string;
        comment: string;
        category: string;
        finalPrice?: number;
        adminImg?: string;
        video?: string;
        adminComment?: string;
      }[];
};
